import styled from 'styled-components';
import { COLORS, device, ROUTES } from '../../constants';
import { NavItem } from './NavItem/NavItem';
import { HeaderInnoLogo } from '../../assets/svg';
import { useTranslation } from 'react-i18next';
import BurgerButton from '../Burger/BurgerButton';
import { useEffect, useState } from 'react';
import BurgerMenu from '../Burger/BurgerMenu';
import { useLocation } from 'react-router-dom';
import { UpdatesButton } from '../Updates/UpdatesButton';
import { UpdatesDropdown } from '../Updates/UpdatesDropdown';
import { Updates } from '../Updates/Updates';
import { usePortfolioContext } from '../../context/PortfolioContext';
import { UpdatesBurger } from '../Updates/UpdatesBurger';

const Header = () => {
  const { t } = useTranslation();
  const [isBurgerActive, setBurgerActive] = useState(false);
  const [isUpdatesShown, setIsUpdatesShown] = useState<boolean>(false);
  const [isUpdatesAvailable, setIsUpdatesAvailable] = useState<boolean>(false);
  const location = useLocation();

  const { newProjects } = usePortfolioContext();

  useEffect(() => {
    if (newProjects.length > 0) {
      setIsUpdatesAvailable(true);
    }
  }, [newProjects]);

  useEffect(() => {
    setIsUpdatesShown(false);
  }, [location.pathname]);

  const onClickBurger = () => {
    document.body.style.overflow = isBurgerActive
      ? 'auto'
      : (document.body.style.overflow = 'hidden');
    setBurgerActive(prev => !prev);
  };

  const isHomeActive =
    location.pathname === ROUTES.HOME ||
    location.pathname === ROUTES.INDUSTRIES;

  const isPortfolioActive =
    location.pathname === ROUTES.PORTFOLIO ||
    location.pathname.startsWith(ROUTES.PROJECT);

  const handleBellButtonClick = () => {
    setIsUpdatesShown(prev => !prev);
  };

  const handleLinkClick = () => {
    setBurgerActive(false);
  };

  return (
    <StyledNav>
      <NavItemsLeft>
        <BurgerButton onClick={onClickBurger} />
        <Logo>
          <HeaderInnoLogo />
        </Logo>
      </NavItemsLeft>
      <Presentation $active={isBurgerActive} onClick={onClickBurger} />
      <BurgerMenu isActive={isBurgerActive} onClick={onClickBurger}>
        <BurgerInner>
          <NavItem
            onClick={onClickBurger}
            path={ROUTES.HOME}
            title={t('screen.home.title')}
          />
          <NavItem
            onClick={onClickBurger}
            path={ROUTES.PORTFOLIO}
            title={t('screen.product.title')}
          />
          <NavItem
            onClick={onClickBurger}
            path={ROUTES.ABOUT}
            title={t('screen.about.title')}
          />
          <NavItem
            onClick={onClickBurger}
            path={ROUTES.CONTACT_US}
            title={t('screen.contact.title')}
          />
          {newProjects.length > 0 && (
            <UpdatesBurger>
              <Updates
                newProjectsList={newProjects}
                visualMode="dropdown"
                onLinkClick={handleLinkClick}
              />
            </UpdatesBurger>
          )}
        </BurgerInner>
      </BurgerMenu>
      <StyledRightPart>
        <StyledNavList>
          <NavItem
            path={ROUTES.HOME}
            className={isHomeActive ? 'active' : ''}
            title={t('screen.home.title')}
          />
          <NavItem path={ROUTES.ABOUT} title={t('screen.about.title')} />
          <NavItem
            className={isPortfolioActive ? 'active' : ''}
            path={ROUTES.PORTFOLIO}
            title={t('screen.product.title')}
          />
          <NavItem path={ROUTES.CONTACT_US} title={t('screen.contact.title')} />
          <UpdatesButton
            isUpdatesAvailable={isUpdatesAvailable}
            isUpdatesShown={isUpdatesShown}
            onClick={handleBellButtonClick}
          />
          {isUpdatesShown && newProjects.length > 0 && (
            <UpdatesDropdown>
              <Updates
                newProjectsList={newProjects}
                visualMode="dropdown"
                onLinkClick={handleBellButtonClick}
              />
            </UpdatesDropdown>
          )}
        </StyledNavList>
      </StyledRightPart>
    </StyledNav>
  );
};

export default Header;

const StyledNav = styled.nav`
  height: 80px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 18px;
  z-index: 50;
  @media ${device.md} {
    font-size: 24px;
    height: 60px;
  }
`;

const NavItemsLeft = styled.div`
  display: flex;
  align-items: center;
  @media screen and ${device.md} {
    gap: 20px;
  }
`;

const StyledNavList = styled.ul`
  display: flex;
  flex-direction: row;
  gap: 40px;
  height: 80px;
  align-items: center;
  position: relative;
  @media ${device.md} {
    gap: 20px;
    display: none;
  }
`;

const StyledRightPart = styled.div`
  display: flex;
  align-items: center;
  gap: 24px;
`;

const Presentation = styled.div<{ $active: boolean }>`
  opacity: ${({ $active }) => ($active ? 1 : 0)};
  visibility: ${({ $active }) => ($active ? 'visible' : 'hidden')};
  transition-property: opacity, visibility;
  transition-duration: 0.2s;
  background-color: rgba(0, 0, 0, 0.6);
  transition-timing-function: ease-in-out;
  position: fixed;
  inset: 0;
  overflow: hidden;
  display: none;
  @media ${device.md} {
    display: block;
  }
`;

export const Logo = styled.div`
  svg {
    color: ${COLORS.WHITE};
    width: auto;
    height: 70px;
  }
  @media screen and ${device.md} {
    svg {
      height: 60px;
    }
  }
`;

const BurgerInner = styled.ul`
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  justify-content: center;
`;
